/**
 * WorksMonthPlanningPanel (iter 102 - Fev 2026)
 *
 * Affiche un mini-Gantt mensuel des interventions de TOUS les projets de la branche.
 * Vue partagee, presente sur le tableau de bord de tous les collaborateurs internes.
 *
 * Couleurs : 1 couleur stable par PROJET (pas par phase) pour distinguer rapidement
 * d'un projet a un autre, meme avec beaucoup d'interventions simultanees.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Palette de 14 couleurs distinctes (tons saturés mais accessibles) -- une par projet
const PROJECT_PALETTE = [
  { bar: 'bg-blue-500',    text: 'text-blue-50',    label: 'blue'    },
  { bar: 'bg-emerald-500', text: 'text-emerald-50', label: 'emerald' },
  { bar: 'bg-amber-500',   text: 'text-amber-50',   label: 'amber'   },
  { bar: 'bg-rose-500',    text: 'text-rose-50',    label: 'rose'    },
  { bar: 'bg-violet-500',  text: 'text-violet-50',  label: 'violet'  },
  { bar: 'bg-cyan-600',    text: 'text-cyan-50',    label: 'cyan'    },
  { bar: 'bg-orange-500',  text: 'text-orange-50',  label: 'orange'  },
  { bar: 'bg-fuchsia-500', text: 'text-fuchsia-50', label: 'fuchsia' },
  { bar: 'bg-teal-600',    text: 'text-teal-50',    label: 'teal'    },
  { bar: 'bg-indigo-500',  text: 'text-indigo-50',  label: 'indigo'  },
  { bar: 'bg-lime-600',    text: 'text-lime-50',    label: 'lime'    },
  { bar: 'bg-pink-500',    text: 'text-pink-50',    label: 'pink'    },
  { bar: 'bg-sky-600',     text: 'text-sky-50',     label: 'sky'     },
  { bar: 'bg-yellow-600',  text: 'text-yellow-50',  label: 'yellow'  },
];

// Hash stable d'une chaine -> index dans la palette (couleur deterministe par projet)
const hashColor = (str) => {
  let h = 0;
  for (let i = 0; i < (str || '').length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return PROJECT_PALETTE[h % PROJECT_PALETTE.length];
};

const STATUS_DOT = {
  planifie: 'bg-slate-300',
  en_cours: 'bg-amber-300',
  termine: 'bg-emerald-300',
};

const WorksMonthPlanningPanel = ({ branch, isConsolidated = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const today = new Date();
  const target = new Date(today.getFullYear(), today.getMonth() + offset, 1);
  const year = target.getFullYear();
  const month = target.getMonth() + 1;

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = { year, month };
    if (branch && !isConsolidated) params.branch = branch;
    axios
      .get(`${API}/works/month-planning`, { params })
      .then((r) => { if (active) setData(r.data); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [branch, isConsolidated, year, month]);

  const monthLabel = target.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const daysInMonth = new Date(year, month, 0).getDate();
  const todayCol = today.getFullYear() === year && today.getMonth() + 1 === month ? today.getDate() : null;

  // Couleur stable assignee par index dans la liste retournee (alterne) -- si meme position
  // sur 2 mois, on garde la meme palette via un hash sur project_id.
  const colorForProject = (project_id) => hashColor(project_id);

  const computeBar = (item) => {
    const s = item.start_date || item.target_date;
    const e = item.end_date || item.start_date || item.target_date;
    if (!s) return null;
    const sDate = new Date(s);
    const eDate = new Date(e || s);
    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month - 1, daysInMonth);
    if (eDate < monthStart || sDate > monthEnd) return null;
    const startDay = sDate < monthStart ? 1 : sDate.getDate();
    const endDay = eDate > monthEnd ? daysInMonth : eDate.getDate();
    return { startDay, endDay, span: endDay - startDay + 1 };
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="works-month-planning-panel">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Planning travaux du mois</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {data?.total_items ?? 0} intervention(s) sur {data?.projects?.length ?? 0} chantier(s) en {monthLabel}.
          </p>
        </div>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <button onClick={() => setOffset((o) => o - 1)} className="px-2.5 py-1 rounded text-sm hover:bg-white" data-testid="works-month-prev">‹</button>
          <button onClick={() => setOffset(0)} className="px-3 py-1 rounded text-xs font-semibold capitalize hover:bg-white" data-testid="works-month-today">{monthLabel}</button>
          <button onClick={() => setOffset((o) => o + 1)} className="px-2.5 py-1 rounded text-sm hover:bg-white" data-testid="works-month-next">›</button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400 italic">Chargement...</p>
      ) : !data || !data.projects || data.projects.length === 0 ? (
        <p className="text-sm text-gray-400 italic" data-testid="works-month-empty">Aucune intervention planifiee ce mois-ci.</p>
      ) : (
        <>
          <div className="overflow-x-auto" data-testid="works-month-gantt">
            <table className="w-full text-xs border-collapse" style={{ minWidth: '900px' }}>
              <thead>
                <tr className="text-[10px] text-gray-500 uppercase">
                  <th className="text-left font-semibold px-2 py-1 w-44 sticky left-0 bg-white z-10">Chantier</th>
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const d = new Date(year, month - 1, i + 1);
                    const dow = d.getDay();
                    const isWeekend = dow === 0 || dow === 6;
                    const isToday = todayCol === i + 1;
                    return (
                      <th
                        key={i}
                        className={`px-0 py-1 text-center font-medium border-l border-gray-100 ${isWeekend ? 'bg-gray-50 text-gray-400' : 'text-gray-600'} ${isToday ? 'bg-[#13736D]/10 text-[#13736D] font-bold' : ''}`}
                        style={{ width: `${100 / daysInMonth}%` }}
                      >
                        {i + 1}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.projects.map((p) => {
                  const c = colorForProject(p.project_id);
                  return (
                    <tr key={p.project_id} className="border-t border-gray-100" data-testid={`works-month-project-${p.project_id}`}>
                      <td className="px-2 py-1.5 sticky left-0 bg-white z-10">
                        <a href={`/projects/${p.project_id}`} className="block hover:underline">
                          <p className="text-sm font-semibold text-gray-900 truncate flex items-center gap-1.5">
                            <span className={`inline-block w-2.5 h-2.5 rounded-sm ${c.bar} flex-shrink-0`}></span>
                            {p.project_name}
                          </p>
                          <p className="text-[10px] text-gray-500 truncate">{p.city || '—'} · {p.client_name || ''}</p>
                        </a>
                      </td>
                      <td colSpan={daysInMonth} className="p-0 relative align-middle" style={{ height: `${Math.max(28, p.items.length * 22)}px` }}>
                        <div className="absolute inset-0 grid pointer-events-none" style={{ gridTemplateColumns: `repeat(${daysInMonth}, minmax(0,1fr))` }}>
                          {Array.from({ length: daysInMonth }).map((_, i) => {
                            const d = new Date(year, month - 1, i + 1);
                            const dow = d.getDay();
                            const isWeekend = dow === 0 || dow === 6;
                            const isToday = todayCol === i + 1;
                            return <div key={i} className={`border-l border-gray-50 ${isWeekend ? 'bg-gray-50/50' : ''} ${isToday ? 'bg-[#13736D]/5' : ''}`} />;
                          })}
                        </div>
                        {p.items.map((it, idx) => {
                          const bar = computeBar(it);
                          if (!bar) return null;
                          const left = ((bar.startDay - 1) / daysInMonth) * 100;
                          const width = (bar.span / daysInMonth) * 100;
                          return (
                            <div
                              key={it.item_id || idx}
                              title={`${p.project_name} · ${it.works_company}${it.works_phase ? ' · ' + it.works_phase : ''} (${it.start_date || ''} → ${it.end_date || it.start_date || ''})`}
                              className={`absolute rounded-md ${c.bar} ${c.text} text-[10px] px-1.5 leading-tight truncate font-semibold shadow-sm hover:brightness-110 hover:shadow-md transition cursor-pointer`}
                              style={{
                                left: `calc(${left}% + 1px)`,
                                width: `calc(${width}% - 2px)`,
                                top: `${idx * 22 + 3}px`,
                                height: '18px',
                                lineHeight: '18px',
                              }}
                              data-testid={`works-month-bar-${it.item_id}`}
                            >
                              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${STATUS_DOT[it.status] || 'bg-slate-200'}`} />
                              {it.works_company}{it.works_phase ? ` · ${it.works_phase}` : ''}
                            </div>
                          );
                        })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-gray-500 mt-3 pt-2 border-t border-gray-100">
            <span className="font-semibold">Statut :</span>
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>Planifie</span>
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>En cours</span>
            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>Termine</span>
            <span className="ml-auto italic">Couleurs = 1 par projet (carré a gauche du nom du chantier)</span>
          </div>
        </>
      )}
    </div>
  );
};

export default WorksMonthPlanningPanel;
